import {Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup, FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {IfeFormat} from '../../../target_systems';
import {ASPECT_RATIOS, ASPECT_RATIO_PASSTHROUGH} from '../../../aspect-ratio';
import {Subscription} from 'rxjs';
import {countInvalidFields} from '../form-messages';
import {SecurityLevelService, WATERMARK_REQUIRED_LEVEL} from '../../../services/security-level.service';

/** template_capabilities.forensic_watermark_selection, parsed. */
export interface WatermarkSelection {
  default: boolean;
  auto_select_min_source_duration_seconds?: number | null;
}


@Component({
    selector: 'app-default-specific',
    templateUrl: './default-specific.component.html',
    styleUrls: ['./default-specific.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => DefaultSpecificComponent),
        },
        {
            provide: NG_VALIDATORS,
            useExisting: DefaultSpecificComponent,
            multi: true
        }],
  imports: [
    FormsModule,
    ReactiveFormsModule
]
})
export class DefaultSpecificComponent implements OnInit, ControlValueAccessor, OnChanges, OnDestroy {

  @Input()
  ife_format: IfeFormat;

  /** The item's source, `bucket/path/`. A pre-release bucket's content must be watermarked (VTK-3545). */
  @Input()
  input_brefix: string;

  /** template_capabilities.supports_forensic_watermark; null while unknown (nothing offered). */
  @Input()
  watermarkSupported: boolean | null = null;

  /** template_capabilities failed for this format; with watermarkSupported null that reads "unknown". */
  @Input()
  capabilitiesFailed = false;

  /** The job-level switch exists only where the template embeds the airline's watermark. */
  get showWatermark(): boolean {
    return this.watermarkSupported !== false || this.watermarkRequired;
  }

  /** How to preselect the switch; null when the template cannot watermark. */
  @Input()
  watermarkSelection: WatermarkSelection | null = null;

  /** The source video's duration in seconds; null while unknown or without a video track. */
  @Input()
  sourceDurationSeconds: number | null = null;

  /** Set once the operator changes the switch: from then on it is never auto-changed. */
  watermarkTouched = false;

  /** The source's bucket requires a forensic watermark; "No watermark" is not offered. */
  watermarkRequired = false;
  private securityLevelSub: Subscription;

  form: FormGroup;

  /** Set by the PO form once a submit was refused: from then on invalid fields show as such. */
  @Input()
  submitted = false;

  /** Whether a control shows its error: invalid, and touched or after a refused submit. */
  showInvalid(name: string): boolean {
    const c = this.form?.controls[name];
    return !!c && c.invalid && (c.touched || this.submitted);
  }
  readonly aspectRatios = ASPECT_RATIOS;
  private changed;
  private touched;

  constructor(private formBuilder: FormBuilder, private securityLevelService: SecurityLevelService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['input_brefix']) {
      this.securityLevelSub?.unsubscribe();
      this.watermarkRequired = false;
      if (this.input_brefix) {
        this.securityLevelSub = this.securityLevelService.securityLevel(`s3://${this.input_brefix}`).subscribe(level => {
          this.watermarkRequired = level === WATERMARK_REQUIRED_LEVEL;
          this.applyWatermarkRules();
        });
      }
    }
    if (this.ife_format && this.form) {
      this.applyFormatControls();
    }
    this.applyWatermarkRules();
  }

  /**
   * The controls only some formats have. Also called from ngOnInit: a format set from the start reaches
   * ngOnChanges before the form exists, and without the control writeValue would drop a restored value and
   * registerOnChange would then hand the parent a value without it.
   */
  private applyFormatControls(): void {
    if (!this.ife_format || !this.form) {
      return;
    }
    if (this.ife_format.require_metadata_reference) {
      if (!this.form.controls['metadata_reference']) {
        this.form.addControl('metadata_reference', new FormControl('', [Validators.required]))
      }
    } else {
      this.form.removeControl('metadata_reference')
    }
  }

  ngOnDestroy(): void {
    this.securityLevelSub?.unsubscribe();
  }

  /** The format cannot watermark, but the source requires it: no valid choice exists. */
  get watermarkImpossible(): boolean {
    return this.watermarkRequired && this.watermarkSupported === false;
  }

  /**
   * Called whenever the format, the security level, the form or its value may have changed. Angular's order is
   * ngOnChanges -> ngOnInit -> writeValue -> registerOnChange -> setDisabledState, and a cached security level
   * arrives synchronously in the first of these, before the form exists -- so every one of them reapplies the rules.
   */
  private applyWatermarkRules(): void {
    if (!this.form) {
      return;
    }
    const watermark = this.form.controls['watermark'];
    if (this.watermarkSupported === null) {
      // Unknown -- still loading, or the capabilities request failed (the notice says which) -- not
      // unsupported. Disabled (nothing to choose yet) but the value is kept, so a restored "insert
      // watermark" survives until support arrives; the PO form refuses to submit meanwhile.
      watermark.disable({emitEvent: false});
    } else if (!this.watermarkSupported) {
      watermark.setValue(false)
      watermark.disable()
    } else {
      // Left enabled even when required: a disabled control is missing from the form's value, and the
      // workflow would then receive no watermark at all. "No watermark" is simply not offered.
      watermark.enable()
      if (this.watermarkRequired) {
        watermark.setValue(true)
      } else if (!this.watermarkTouched) {
        watermark.setValue(this.autoWatermark());
      }
    }
    watermark.updateValueAndValidity();
  }

  /**
   * The switch's preselection: the template's default, or on when the source video is at least
   * the template's threshold long. No video duration (no video track) selects nothing.
   */
  private autoWatermark(): boolean {
    const sel = this.watermarkSelection;
    if (!sel) {
      return false;
    }
    const min = sel.auto_select_min_source_duration_seconds;
    const long = typeof min === 'number' && typeof this.sourceDurationSeconds === 'number'
      && this.sourceDurationSeconds >= min;
    return !!sel.default || long;
  }

  /** The operator changed the switch. */
  onWatermarkChange(): void {
    this.watermarkTouched = true;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable()
    } else {
      this.form.enable()
      // enable() also re-enables a watermark the format cannot carry; Angular calls this on setup, too.
      this.applyWatermarkRules();
    }
  }


  writeValue(value) {
    // A restored PO's watermark choice is the operator's: never auto-changed.
    if (typeof value?.watermark === 'boolean') {
      this.watermarkTouched = true;
    }
    this.form.patchValue(value, {emitEvent: false})
    // A restored PO (saved state, "Load PO") may say "no watermark" for a source that requires one.
    this.applyWatermarkRules();
  }

  registerOnChange(fn) {
    this.changed = fn;
    // The rules may already have changed the value (see applyWatermarkRules) before the parent was listening,
    // and the parent's value is what gets submitted.
    fn(this.form.value);
  }

  registerOnTouched(fn) {
    this.touched = fn;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      watermark: [false, [Validators.nullValidator]],
      aspect_ratio: [ASPECT_RATIO_PASSTHROUGH, [Validators.required]],
    })
    this.form.valueChanges.subscribe((val) => {
      if (this.touched) {
        this.touched();
      }
      if (this.changed) {
        this.changed(val);
      }
    });
    this.applyFormatControls();
    this.applyWatermarkRules();
  }

  validate({value}: FormControl) {
    if (this.watermarkImpossible) {
      // Not a field: no input to mark or focus. Counted as fields are only real inputs; the reason the
      // submit is blocked anyway travels as a sentence of its own (form-messages: 'blocking').
      return {
        watermarkRequired: true,
        invalidFields: countInvalidFields(this.form),
        blocking: 'This target format cannot insert the forensic watermark this pre-release source requires.',
      };
    }
    const isNotValid = !this.form.valid
    return isNotValid && {
      invalid: true,
      invalidFields: countInvalidFields(this.form),
    }
  }


}
